import { DialogComponent } from '@theme/dialog';

/**
 * Drawer Component
 * Handles opening and closing of drawer dialogs
 */
export class DrawerComponent extends DialogComponent {
  /** @type {NodeListOf<HTMLButtonElement>} */
  toggleButtons;
  /** @type {NodeListOf<HTMLButtonElement>} */
  closeButtons;
  /** @type {HTMLElement | null} */
  triggerElement = null;
  /** @type {boolean} */
  #openedViaKeyboard = false;

  connectedCallback() {
    super.connectedCallback();

    this.drawerId = this.getAttribute('data-drawer-id');
    this.toggleButtons = document.querySelectorAll(`[data-open-drawer-id="${this.drawerId}"]`);
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');
    
    this.bindEvents();
  }

  bindEvents() {
    for (const button of this.toggleButtons) {
      button.addEventListener('click', this.handleToggle);
      button.addEventListener('keydown', this.handleKeyboardOpen);
    }

    for (const button of this.closeButtons) {
      button.addEventListener('click', this.handleClose);
    }

    this.addEventListener('dialog:close', this.handleDialogClose);
  }

  handleKeyboardOpen = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      this.#openedViaKeyboard = true;
    }
  };

  showDialog() {
    this.triggerElement = /** @type {HTMLElement | null} */ (document.activeElement);
    super.showDialog();
    
    const firstFocusable = /** @type {HTMLElement | null} */ (this.querySelector('[data-drawer-close]'));
    if (firstFocusable) {
      if (this.#openedViaKeyboard) {
        firstFocusable.focus({ preventScroll: true });
      } else {
        firstFocusable.focus({ preventScroll: true });
        firstFocusable.classList.add('focus-not-visible');
        firstFocusable.addEventListener('blur', () => {
          firstFocusable.classList.remove('focus-not-visible');
        }, { once: true });
        firstFocusable.addEventListener('keydown', () => {
          firstFocusable.classList.remove('focus-not-visible');
        }, { once: true });
      }
    }
    this.#openedViaKeyboard = false;
  }

  handleToggle = (/** @type {Event} */ event) => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  handleClose = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.closeDialog();
  };

  handleDialogClose = () => {
    if (this.triggerElement) {
      this.triggerElement.focus({ preventScroll: true });
      this.triggerElement = null;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    
    for (const button of this.toggleButtons) {
      button.removeEventListener('click', this.handleToggle);
      button.removeEventListener('keydown', this.handleKeyboardOpen);
    }

    for (const button of this.closeButtons) {
      button.removeEventListener('click', this.handleClose);
    }

    this.removeEventListener('dialog:close', this.handleDialogClose);
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}

